import type { ReactNode } from 'react';
import { isSchemaWithoutFk, type ResolvedImpact } from '@visualitics-toolkit/shared';

import { Badge, Button, Checkbox } from '../atoms';
import { ConfirmTokenField, SeverityBadge, formatDateTime } from '../molecules';

export interface ConfirmationState {
  ackImpact: boolean;
  typedToken: string;
}

/**
 * Painel de impacto: severidade, consequências e as tabelas que serão TOCADAS com
 * contagem real do preflight. Os checkboxes aqui são atrito de UI — o gate é servidor.
 */
export function ImpactPanel({
  impact,
  expectedToken,
  confirmation,
  onConfirmationChange,
  onRequestBackup,
}: {
  impact: ResolvedImpact;
  expectedToken?: string | null;
  confirmation: ConfirmationState;
  onConfirmationChange: (next: ConfirmationState) => void;
  onRequestBackup: () => void;
}): ReactNode {
  const totalRows = impact.tables.reduce((sum, table) => sum + (table.rowCount ?? 0), 0);

  return (
    <section className="card space-y-4" aria-label="Impacto da operação" data-testid="impact-panel">
      <header className="flex flex-wrap items-center gap-2">
        <SeverityBadge severity={impact.severity} />
        <Badge>reversibilidade: {impact.reversibility}</Badge>
        {totalRows > 0 && <Badge tone="warning">{totalRows.toLocaleString('pt-BR')} linha(s)</Badge>}
      </header>

      <ul className="space-y-1 text-sm text-content">
        {impact.consequences.map((consequence) => (
          <li key={consequence} className="flex gap-2">
            <span aria-hidden className="text-muted">
              •
            </span>
            {consequence}
          </li>
        ))}
      </ul>

      {impact.tables.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs uppercase text-muted">
              <tr>
                <th className="px-3 py-2">Tabela</th>
                <th className="px-3 py-2">Ação</th>
                <th className="px-3 py-2">Chave</th>
                <th className="px-3 py-2 text-right">Linhas</th>
              </tr>
            </thead>
            <tbody>
              {impact.tables.map((table) => (
                <tr
                  key={`${table.schema}.${table.table}`}
                  className={isSchemaWithoutFk(table.schema) ? 'bg-severity-destructive/10' : undefined}
                >
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {table.schema}.{table.table}
                    {table.withoutFk && (
                      <span
                        className="ml-2 text-[11px] text-severity-destructive"
                        title="Schema sem FK para organizations: nada é apagado em cascata"
                      >
                        sem FK
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">{table.action}</td>
                  <td className="px-3 py-1.5 font-mono text-xs text-muted">{table.keyColumn}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {table.rowCount === null ? '—' : table.rowCount.toLocaleString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {impact.backup.requiredWithinHours !== null && (
        <div
          className={
            impact.backup.satisfied
              ? 'rounded-md border border-severity-reversible/50 bg-severity-reversible/10 px-3 py-2 text-sm'
              : 'flex items-center justify-between gap-3 rounded-md border border-severity-irreversible/60 bg-severity-irreversible/10 px-3 py-2 text-sm'
          }
        >
          <span>
            Backup exigido nas últimas {impact.backup.requiredWithinHours}h. Último:{' '}
            {formatDateTime(impact.backup.lastCompletedAt)}.
          </span>
          {!impact.backup.satisfied && (
            <Button variant="primary" onClick={onRequestBackup}>
              Fazer backup agora
            </Button>
          )}
        </div>
      )}

      {impact.requiresConfirmation && (
        <div className="space-y-3 border-t border-border pt-3">
          <Checkbox
            label="Li e compreendo o impacto descrito acima."
            checked={confirmation.ackImpact}
            onChange={(event) =>
              onConfirmationChange({ ...confirmation, ackImpact: event.target.checked })
            }
          />
          {impact.typedToken && (
            <ConfirmTokenField
              kind={impact.typedToken.kind}
              hint={impact.typedToken.hint}
              expected={expectedToken}
              value={confirmation.typedToken}
              onChange={(typedToken) => onConfirmationChange({ ...confirmation, typedToken })}
            />
          )}
        </div>
      )}
    </section>
  );
}
